import React, { Dispatch, SetStateAction, useMemo } from 'react';
import { Button, CircularProgress, Dialog, DialogContent, Typography } from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import { catchErrorMessage } from 'utility/helpers';
import { toast } from 'react-toastify';
import { FormProvider, useForm } from 'react-hook-form';
import useUserData from 'hooks/useUserData';
import UserAPI from 'api/UserAPI';
import { styled } from '@mui/material/styles';
import CustomDialogTitle from 'components/modal/CustomDialogTitle';
import { updateUserDetailsCache } from '@buildwise/libs/people/queries/useUserProfileData';
import InteractionsForm from './InteractionsForm';
import {
  CONFIGS,
  getCustomFieldsDefaultValues,
  useConfigQuery,
} from '@buildwise/libs/configurations';
import { CustomFieldValueType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import { ConfigFieldEntryType } from 'config/config';

export type NotificationsForm = {
  topicsForNotifications: string[];
  postNotificationFrequency: string;
  questionNotificationFrequency: string;
  suggestionNotificationFrequency: string;
  emailNotificationSettings: {
    postNotificationFrequency: string;
    questionNotificationFrequency: string;
    suggestionNotificationFrequency: string;
    notifyWhenOthersCommentOnMyArticlesFlag: boolean;
    notifyWhenOthersReplyToMyCommentsFlag: boolean;
  };
  pushNotificationSettings: {
    postNotificationFrequency: string;
    questionNotificationFrequency: string;
    suggestionNotificationFrequency: string;
    notifyWhenOthersCommentOnMyArticlesFlag: boolean;
    notifyWhenOthersReplyToMyCommentsFlag: boolean;
  };
  region?: string;
  department?: string;
};

type NotificationSettingsModalProps = {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
};

const getFrequencyDefaultValue = (notificationSettings, key) => {
  const emailFrequency = notificationSettings?.emailNotificationSettings?.[key];
  const pushFrequency = notificationSettings?.pushNotificationSettings?.[key];

  if (emailFrequency === 'NEVER' && pushFrequency === 'NEVER') return 'NEVER';
  if (!emailFrequency && !pushFrequency) return 'DAILY';

  return emailFrequency !== 'NEVER' ? emailFrequency : pushFrequency;
};

const NotificationSettingsModal: React.FC<NotificationSettingsModalProps> = ({ open, setOpen }) => {
  const { user, companyId } = useUserData();
  const { activeUniqueEntries: customInputFields, isFetched: isCustomFieldsFetched } =
    useConfigQuery({
      companyId,
      configName: CONFIGS.USER_INPUT_FIELDS.configName,
    });

  const requiredInputFields = useMemo(
    () => customInputFields.filter((field) => ['region', 'department'].includes(field.entryCode)),
    [customInputFields],
  );

  const getDefaultValues = () => {
    return {
      ...getCustomFieldsDefaultValues({ data: user, fields: requiredInputFields }),
      postNotificationFrequency: getFrequencyDefaultValue(
        user?.notificationSettings,
        'postNotificationFrequency',
      ),
      questionNotificationFrequency: getFrequencyDefaultValue(
        user?.notificationSettings,
        'questionNotificationFrequency',
      ),
      suggestionNotificationFrequency: getFrequencyDefaultValue(
        user?.notificationSettings,
        'suggestionNotificationFrequency',
      ),
      emailNotificationSettings: {
        postNotificationFrequency:
          user?.notificationSettings?.emailNotificationSettings?.postNotificationFrequency ||
          'DAILY',
        questionNotificationFrequency:
          user?.notificationSettings?.emailNotificationSettings?.questionNotificationFrequency ||
          'REAL_TIME',
        suggestionNotificationFrequency:
          user?.notificationSettings?.emailNotificationSettings?.suggestionNotificationFrequency ||
          'REAL_TIME',
        notifyWhenOthersCommentOnMyArticlesFlag:
          user?.notificationSettings?.emailNotificationSettings
            ?.notifyWhenOthersCommentOnMyArticlesFlag || false,
        notifyWhenOthersReplyToMyCommentsFlag:
          user?.notificationSettings?.emailNotificationSettings
            ?.notifyWhenOthersReplyToMyCommentsFlag || false,
      },
      pushNotificationSettings: {
        postNotificationFrequency:
          user?.notificationSettings?.pushNotificationSettings?.postNotificationFrequency ||
          'DAILY',
        questionNotificationFrequency:
          user?.notificationSettings?.pushNotificationSettings?.questionNotificationFrequency ||
          'REAL_TIME',
        suggestionNotificationFrequency:
          user?.notificationSettings?.pushNotificationSettings?.suggestionNotificationFrequency ||
          'REAL_TIME',
        notifyWhenOthersCommentOnMyArticlesFlag:
          user?.notificationSettings?.pushNotificationSettings
            ?.notifyWhenOthersCommentOnMyArticlesFlag || false,
        notifyWhenOthersReplyToMyCommentsFlag:
          user?.notificationSettings?.pushNotificationSettings
            ?.notifyWhenOthersReplyToMyCommentsFlag || false,
      },
    };
  };
  const notificationsForm = useForm<NotificationsForm>({
    mode: 'onTouched',
    defaultValues: getDefaultValues(),
  });
  const {
    formState: { isValid, isDirty, isSubmitting },
    handleSubmit,
    reset,
  } = notificationsForm;

  const closeModal = () => {
    setOpen(false);
    reset();
  };

  const onSubmit = async (values: NotificationsForm) => {
    try {
      const payload = {
        ...user,
        notificationSettings: {
          ...user?.notificationSettings,
          emailNotificationSettings: {
            ...user?.notificationSettings?.emailNotificationSettings,
            ...values?.emailNotificationSettings,
          },
          pushNotificationSettings: {
            ...user?.notificationSettings?.pushNotificationSettings,
            ...values?.pushNotificationSettings,
          },
        },
      };

      // Custom user field values
      const saveAsLists = ['department', 'region'];
      const customFieldsValues: Record<string, CustomFieldValueType> = {};
      customInputFields.forEach((field) => {
        if (
          Array.isArray(values[field.entryCode])
            ? values[field.entryCode].length > 0
            : values[field.entryCode]
        ) {
          if (
            saveAsLists.includes(field.entryCode) ||
            field.entryType?.toUpperCase() !== ConfigFieldEntryType.CUSTOM
          ) {
            payload[field.entryCode] =
              saveAsLists.includes(field.entryCode) && field.referenceObject?.type === 'SELECT'
                ? [values[field.entryCode]]
                : values[field.entryCode];
          } else {
            customFieldsValues[field.entryCode] = {
              fieldName: field.entryCode,
              fieldValue:
                saveAsLists.includes(field.entryCode) && field.referenceObject?.type === 'SELECT'
                  ? [values[field.entryCode]]
                  : values[field.entryCode],
              type: field.referenceObject?.type || '',
              configRefName: field.referenceObject?.configRefName || '',
              label: field.referenceObject?.label || field.entryDescription,
            };
          }
        }
      });
      payload['customFields'] = customFieldsValues;
      const { data } = await UserAPI.editUser(payload);
      toast.success('Notification Settings Updated');
      updateUserDetailsCache({ ...user, ...data });
      reset();
      closeModal();
    } catch (e) {
      toast.error(catchErrorMessage(e));
    }
  };
  const triggerSubmit = handleSubmit(onSubmit);

  return (
    <Dialog
      open={open}
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
      maxWidth="sm"
      fullWidth
    >
      <CustomDialogTitle onClose={closeModal} id="dialog-title">
        <$Title>Notification Settings</$Title>
      </CustomDialogTitle>
      <DialogContent dividers sx={{ pt: 0 }}>
        <FormProvider {...notificationsForm}>
          <InteractionsForm requiredInputFields={requiredInputFields} />
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <Button
          disabled={isSubmitting || !isDirty || !isValid}
          onClick={triggerSubmit}
          variant="contained"
        >
          {isSubmitting ? <CircularProgress sx={{ color: '#000' }} size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const $Title = styled(Typography, { label: 'InteractionsDialogTitle' })({
  fontSize: '24px',
  fontWeight: 700,
  color: 'black',
});

export default NotificationSettingsModal;
